import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";
import type { ReactNode } from "react";

import { ApiError, NetworkError } from "@/shared/api";

/**
 * Дефолты подобраны под жёсткий rate limit бэкенда: 60 запросов в минуту на
 * чтение и 10 на денежные ручки, причём корзина считается **по IP**, а не по
 * пользователю. В dev три демо-аккаунта делят одну корзину, поэтому любой
 * автоматический опрос упирается в 429 на всю машину.
 *
 * Отсюда два запрета: никакого `refetchInterval` и выключенный
 * `refetchOnWindowFocus` — Telegram WebView щедро сыплет событиями фокуса
 * при каждом сворачивании клавиатуры.
 *
 * Единственное исключение — две ручки экрана робота
 * (`entities/robot/model/queries.ts`): §4.2 доработок требует обновлять
 * метрики раз в 30–60 секунд. Оно посчитано и локально: два запроса в минуту
 * из шестидесяти, только пока экран открыт и вкладка на переднем плане. Любой
 * следующий интервал обязан считаться так же — здесь общий лимит, а не
 * настройка конкретного экрана.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchInterval: false,
        // Повторять имеет смысл только то, что может измениться само:
        // обрыв сети и пятисотку. Повтор 404 или 400 вернёт тот же ответ,
        // зато потратит запрос из лимита — а он считается по IP, то есть
        // общий на всех, кто сидит за одним адресом.
        retry: (failureCount, error) => {
          if (failureCount >= 2) return false;
          if (error instanceof NetworkError) return true;
          return error instanceof ApiError && error.status >= 500;
        },
      },
      // Ретрай мутации решает вызывающий код — и обязан переиспользовать тот
      // же Idempotency-Key, иначе получится вторая покупка.
      mutations: { retry: false },
    },
  });
}

export function QueryProvider({ children }: { children: ReactNode }) {
  const [client] = useState(createQueryClient);
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
